import type { ErrorResponse, VerjaError } from '@/models/ErrorResponse'

export class BackendError extends Error {
    errors: Record<string, VerjaError[]>;

    constructor(message: string, errors: Record<string, VerjaError[]> = {}) {
        super(message);
        this.errors = errors;
    }
}

export class Backend {
    protected baseUrl: string = '/api';

    protected async request<T>(path: string, options: RequestInit = {}): Promise<T> {
        const url = `${this.baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;

        const defaultHeaders = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };

        const request = new Request(url, {
            ...options,
            headers: {
                ...defaultHeaders,
                ...options.headers,
            },
        });

        const response = await fetch(request);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new BackendError(errorData.description || errorData.message || `HTTP Error ${response.status}`, errorData.errors ?? {});
        }

        if (response.status === 204) return {} as T;

        return response.json();
    }

    protected get<T>(url: string, options?: RequestInit): Promise<T> {
        return this.request<T>(url, { ...options, method: 'GET' });
    }

    protected post<T>(url: string, data?: any, options?: RequestInit): Promise<T> {
        return this.request<T>(url, {
            ...options,
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined
        });
    }

    protected put<T>(url: string, data?: any, options?: RequestInit): Promise<T> {
        return this.request<T>(url, {
            ...options,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined
        });
    }

    protected patch<T>(url: string, data?: any, options?: RequestInit): Promise<T> {
        return this.request<T>(url, {
            ...options,
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined
        });
    }

    protected delete<T>(url: string, data?: any, options?: RequestInit): Promise<T> {
        return this.request<T>(url, {
            ...options,
            method: 'DELETE',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
}
